import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { FunnelIcon, XIcon } from '../AriesIcons';

/**
 * AriesFilterPanel — the standardized filter "layout-signature" for the Aries
 * redesign. It owns the shared popover shell (a funnel trigger that opens an
 * `aries-popover` with a "Filter by:" header + close X) and renders arbitrary
 * filter `children` inside it. Compose the body from `AriesFilterGroup`
 * sections so every feature's filter looks and behaves the same.
 *
 * Reference layout: Dashboard "Total Jobs Due This Month" filter.
 */
const AriesFilterPanel = ({
  isOpen,
  onToggle,
  onClose,
  children,
  footer,
  triggerLabel,
  className,
}) => {
  const intl = useIntl();
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [onClose]);

  return (
    <div className={`aries-filter-panel ${className}`} ref={ref}>
      <button
        type="button"
        className="aries-filter-trigger"
        aria-label={intl.formatMessage({
          id: triggerLabel,
          defaultMessage: triggerLabel,
        })}
        onClick={onToggle}
      >
        <FunnelIcon size={18} />
        <span className="aries-filter-trigger-label">
          {intl.formatMessage({
            id: triggerLabel,
            defaultMessage: triggerLabel,
          })}
        </span>
      </button>
      {isOpen && (
        <div className="aries-filter-popover aries-popover">
          <div className="aries-filter-popover-head">
            <span className="aries-filter-popover-title">
              {intl.formatMessage({
                id: 'Filter by:',
                defaultMessage: 'Filter by:',
              })}
            </span>
            <button
              type="button"
              className="aries-filter-popover-close"
              aria-label={intl.formatMessage({
                id: 'Close',
                defaultMessage: 'Close',
              })}
              onClick={onClose}
            >
              <XIcon size={18} />
            </button>
          </div>
          <div className="aries-filter-popover-body">{children}</div>
          {footer && (
            <div className="aries-filter-popover-footer">{footer}</div>
          )}
        </div>
      )}
    </div>
  );
};

AriesFilterPanel.propTypes = {
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  onClose: PropTypes.func,
  children: PropTypes.node,
  footer: PropTypes.node,
  triggerLabel: PropTypes.string,
  className: PropTypes.string,
};

AriesFilterPanel.defaultProps = {
  isOpen: false,
  onToggle: () => {},
  onClose: () => {},
  children: null,
  footer: null,
  triggerLabel: 'Filter',
  className: '',
};

export default AriesFilterPanel;
